import EFPicture from '@elem/ui/ef-picture';
import { useRouter } from 'next/router';
import { FormatMoneyLocal } from 'eco-design-micro/price/FormatMoneyLocal';
import css from './index.module.scss';
import AddReduceCalc from './components/AddReduceCalc';
import useGetProducts from './hooks/useGetProducts';
import { EFProductsProps, ProductOptions, ProductSubType, ProductType } from './type';

/**
 *
 * 维护者：Hzih.Huang（黄子豪）
 * 产品卡片组件
 *
 */
function EFProducts({ products, BeforeButton, onBuyNow, onLearnMore }: EFProductsProps) {
    const { locale = 'us', query } = useRouter();

    const { curProducts, handlerChangeProducts } = useGetProducts(products);

    const handleCalculate = (data: ProductSubType, n: number, idx: number) => {
        const tempBundles = JSON.parse(JSON.stringify(curProducts));
        const parentIdx = tempBundles?.findIndex((bundle: any) => bundle?.variantId === data?.parentVariantId);
        tempBundles[parentIdx].subs[idx].quantity = n;
        handlerChangeProducts(tempBundles);
    };

    // 计算子产品价格之合
    const calcPrice = (price: number, subs?: ProductSubType[], key: 'price' | 'compareAtPrice' = 'price') => {
        let total = 0;
        subs?.forEach((sub) => {
            total += (sub?.[key] ?? 0) * (sub?.quantity || 0);
        });

        return FormatMoneyLocal((price ?? 0) + total, locale);
    };

    const timer = query.timer ? new Date(`${query?.timer} GMT+8`) : new Date();
    const getBuyNowText = (i: ProductType) => {
        if (i.options?.buyNowTimeTexts) {
            // 获取当前时间区间
            const now = new Date(timer);
            const buyNowTimeTexts = i.options.buyNowTimeTexts;
            const currentBuyNow = buyNowTimeTexts.find((i) => {
                const buyNowTime = new Date(i?.endTime ?? '');
                if (now >= buyNowTime) return false;
                return true;
            });
            return currentBuyNow;
        }
        return {
            text: 'Buy Now',
        };
    };

    const getComingSoonState = (data?: ProductOptions) => {
        const comingSoon = data?.comingSoon;
        if (comingSoon && comingSoon?.endTime) {
            const endTime = new Date(comingSoon.endTime);
            return timer < endTime;
        }
        return false;
    };

    return (
        <ul className={`ef-products ${css.EFProducts}`}>
            {curProducts.map((i, idx) => (
                <li className="ef-products__item" key={idx}>
                    <div className="product-content">
                        <p className="product-title">{i.title}</p>
                        <div className="product-img">
                            <EFPicture src={i.img ?? ''} alt={i.title} />
                        </div>
                        <p className="product-name">{i.name}</p>
                        <p className="product-power">{i.power}</p>
                        <p className="product-desc">{i.desc}</p>
                        {i?.subs?.map((j, idx2) => (
                            <div className="product-sub" key={`${idx}-${idx2}`}>
                                {j.question && <p className="product-question">{j.question}</p>}
                                <div className="sub-box">
                                    <div className="sub-img">
                                        <EFPicture src={j.img ?? ''} alt={j.name} />
                                    </div>
                                    <div className="sub-info">
                                        <p className="sub-name">{j.name}</p>
                                        <div className="sub-price">
                                            <span className="price-discount">{FormatMoneyLocal(j.price ?? 0, locale)}</span>
                                            {j.compareAtPrice && j.price !== j.compareAtPrice && (
                                                <span className="price-original">{FormatMoneyLocal(j.compareAtPrice, locale)}</span>
                                            )}
                                        </div>
                                    </div>
                                    <AddReduceCalc data={j} callback={(data: ProductSubType, n: number) => handleCalculate(data, n, idx2)} />
                                </div>
                            </div>
                        ))}
                    </div>
                    {getComingSoonState(i.options) ? (
                        <div className="product-bottom">
                            <button className=" buy-now disable">Coming Soon</button>
                        </div>
                    ) : (
                        <div className="product-bottom">
                            <div className="total-price">
                                <span className="total-discount">{calcPrice(i.price as number, i.subs)}</span>
                                {i.compareAtPrice !== i.price && (
                                    <span className="total-original">{calcPrice(i.compareAtPrice as number, i.subs, 'compareAtPrice')}</span>
                                )}
                            </div>
                            {BeforeButton && typeof BeforeButton === 'function' ? BeforeButton(i) : BeforeButton}
                            <button
                                className={`buy-now ${(i.inventory ?? 0) <= 0 || getBuyNowText(i)?.disabled ? 'disable' : ''}`}
                                onClick={(i.inventory ?? 0) <= 0 || getBuyNowText(i)?.disabled ? () => undefined : () => onBuyNow?.(i)}
                            >
                                {getBuyNowText(i)?.text}
                            </button>
                            {onLearnMore && (
                                <button className="learn-more3" onClick={() => onLearnMore?.(i)}>
                                    Learn More
                                </button>
                            )}
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
}

export type { ProductType, ProductSubType, ProductOptions } from './type';

export default EFProducts;
